import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

export interface AssetAllocation {
  category: string
  percentage: number
  subcategories?: Array<{
    name: string
    percentage: number
  }>
}

export const useAssetAllocation = (portfolio: string) => {
  const [allocation, setAllocation] = useState<AssetAllocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAllocation = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log(`🚀 Fetching allocation for ${portfolio} (optimized)`)

        // Call the optimized edge function with database caching
        const { data, error: fetchError } = await supabase.functions.invoke(
          'portfolio-asset-allocation',
          {
            body: { portfolio }
          }
        )

        if (fetchError) {
          throw fetchError
        }

        // A response that states its own error is a FAILURE, whatever it carries alongside it.
        // This guard used to AND the error field with an emptiness test on the allocation, so it
        // only fired when the payload was BOTH errored and empty. Every error-bearing 200 the
        // edge function emitted also carried a two-entry allocation, so the emptiness operand was
        // false in exactly the cases the guard existed to catch: the else arm ran setError(null)
        // and the invented split rendered with the caveat actively cleared. Branch on the error
        // alone, and show nothing rather than partial or stale rows — the consuming pages already
        // render an explicit "unavailable" block for an empty array.
        // (Phrased in prose deliberately: tests/portfolios/no-invented-numbers.test.ts scans
        // source TEXT, so spelling the old expression out here would re-trip its RULE B.)
        if (data?.error) {
          console.warn('Asset allocation error:', data.error)
          setError(data.error)
          setAllocation([])
          return
        }
        setError(null)

        // Log performance info
        if (data.cached) {
          console.log(`✅ Served from cache (${data.data_age_hours?.toFixed(1)} hours old)`)
        } else {
          console.log(`🔄 Fresh data fetched for ${portfolio}`)
        }

        setAllocation(data.allocation || [])
      } catch (err) {
        // WHAT WAS HERE. A hard-coded seven-line P2 allocation summing to exactly 100.0, and
        // `portfolio.startsWith('P') ? 60 : 80` Fixed Income for everything else. No source ever
        // produced those numbers, nothing on screen said they were approximate, and the sum is
        // what made them read as authoritative. R5, described on its own page as a 90% equity
        // strategy, rendered as 80% Fixed Income / 20% Equity. A portfolio page must never show
        // an invented allocation as the live one, so a failure now yields no rows at all.
        console.error('Error fetching asset allocation:', err)

        // The message is fixed client copy rather than err.message: this string is rendered
        // verbatim to the public (src/pages/portfolios/*.tsx), and the errors that reach here are
        // transport-level - supabase-js hands back "Edge Function returned a non-2xx status code"
        // for any non-2xx, which is not something to print on a portfolio page. The underlying
        // error is on the console line above.
        setError('Allocation data is temporarily unavailable.')
        setAllocation([])
      } finally {
        setLoading(false)
      }
    }

    if (portfolio) {
      fetchAllocation()
    }
  }, [portfolio])

  return { allocation, loading, error }
}